// Private types ---------------------------------------------------------------

type RegistrationState =
  | { readonly status: 'idle' | 'disconnected' | 'destroyed' }
  | { readonly status: 'pending'; readonly controller: AbortController }

// Public types ----------------------------------------------------------------

export type PushRegistration = {
  readonly disconnect: () => void
  readonly reconnect: () => void
  readonly destroy: () => void
}

export type PushDependencies = {
  readonly permission: () => NotificationPermission
  readonly requestPermission: () => Promise<NotificationPermission>
  readonly fetchToken: (signal: AbortSignal) => Promise<string | null>
  readonly register: (token: string) => void
  readonly reportError: (error: unknown) => void
}

// Public functions ------------------------------------------------------------

// Keeps one registration in flight and limits its effects to the current connection.
export const setupPushRegistration = (
  host: HTMLElement,
  dependencies: PushDependencies
): PushRegistration => {
  let state: RegistrationState = { status: 'idle' }

  // Requests permission synchronously so the browser sees the original click.
  const start = async (
    permission: () => Promise<NotificationPermission>
  ): Promise<void> => {
    if (state.status !== 'idle') {
      return
    }

    const controller = new AbortController()
    const { signal } = controller
    state = { status: 'pending', controller }

    try {
      const result = await permission()
      if (result === 'granted' && !signal.aborted) {
        const token = await dependencies.fetchToken(signal)
        if (token && !signal.aborted) {
          dependencies.register(token)
        }
      }
    } catch (error) {
      if (!signal.aborted) {
        dependencies.reportError(error)
      }
    } finally {
      if (!signal.aborted) {
        state = { status: 'idle' }
      }
    }
  }

  // Refreshes existing permission without asking the user again.
  const registerGranted = (): void => {
    if (dependencies.permission() === 'granted') {
      start(() => Promise.resolve('granted'))
    }
  }

  // Handles only the explicit enable action from the communication page.
  const enable = (): void => {
    start(dependencies.requestPermission)
  }

  // Invalidates continuations before a connection change or hook destruction.
  const stop = (status: 'disconnected' | 'destroyed'): void => {
    if (state.status === 'pending') {
      state.controller.abort()
    }
    state = { status }
  }

  host.addEventListener('web-push:enable', enable)
  registerGranted()

  return {
    disconnect: () => {
      if (state.status !== 'destroyed') {
        stop('disconnected')
      }
    },
    reconnect: () => {
      if (state.status === 'disconnected') {
        state = { status: 'idle' }
        registerGranted()
      }
    },
    destroy: () => {
      stop('destroyed')
      host.removeEventListener('web-push:enable', enable)
    }
  }
}

// Keeps prompt visibility in sync without letting a late query outlive its host.
export const setupPushPrompt = (
  host: HTMLElement,
  queryPermission: () => Promise<
    Pick<PermissionStatus, 'state' | 'addEventListener'>
  >
): (() => void) => {
  const controller = new AbortController()
  const { signal } = controller

  queryPermission()
    .then((status) => {
      if (signal.aborted) {
        return
      }

      // Reflects only browser permission state; no messaging SDK is required.
      const sync = (): void => {
        host.hidden = status.state !== 'prompt'
      }
      sync()
      status.addEventListener('change', sync, { signal })
    })
    .catch(() => {
      // Browsers without notification permission queries keep the optional prompt hidden.
    })

  return () => controller.abort()
}
